import React, { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useParams, useSearchParams } from "react-router";
import useGetAllBooking from "../../../../services/booking/hooks/useGetAllBooking";

const RegistrationFormComponent: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const { data: booking, isFetching, isSuccess } = useGetAllBooking({
        filters: {
            invoice_number: id
        }
    });

    const [searchParams] = useSearchParams();
    const pdfRef = useRef<HTMLDivElement>(null);

    const isDownloadAction = searchParams.has("download"); // Check if 'download' is in URL

    const generatePDF = React.useCallback(async () => {
        const input = pdfRef.current;
        if (!input) return;

        const canvas = await html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
        });
        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4"
        });

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
        }

        pdf.save(`Registration_Form-${id}.pdf`);
    }, [id]);

    useEffect(() => {
        if (!isFetching && booking && isDownloadAction) {
            console.log("Download action detected - redirecting to backend API");
            const apiUrl = import.meta.env.VITE_API_URL;
            if (apiUrl) {
                window.location.href = `${apiUrl}/v4/registration-form/${id}`;
            } else {
                generatePDF();
            }
        }
    }, [isFetching, booking, isDownloadAction, id, generatePDF]);

    if (!id) {
        return (
            <h4 className="text-primary text-center fw-bold">Invoice not found</h4>
        );
    }

    if (!isFetching && booking && booking.data.length === 0) {
        return (
            <h4 className="text-primary text-center fw-bold">Invoice not found</h4>
        );
    }

    return (
        <>
            {isFetching && <h4 className="text-primary text-center fw-bold">Loading...</h4>}
            {!isFetching && isSuccess && booking && booking.data.length > 0 && (
                <div className="bg-white min-vh-100">
                    <div ref={pdfRef} className="mt-3 bg-white p-3" style={{ width: "210mm", margin: "auto", boxSizing: 'border-box' }}>
                        {booking.data.map((item, index) => (
                            <div key={item.id} style={{ pageBreakAfter: index === booking.data.length - 1 ? "auto" : "always" }}>
                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-center border-bottom border-dark border-2">
                                    <ul className="list-unstyled text-start pe-4">
                                        <li className="fw-bold fs-3">{item?.property?.name || "N/A"}</li>
                                        <li className="fs-4">
                                            {[
                                                item?.property?.street_address,
                                                item?.property?.state,
                                                item?.property?.city,
                                                item?.property?.country,
                                            ]
                                                .filter(Boolean)
                                                .join(", ") || "N/A"}
                                        </li>
                                        <li className="fs-4">Indonesia</li>
                                        <li className="fs-4">Phone: <span className="fw-semibold">{item?.property?.primary_phone_country_code || ""} {item?.property?.primary_phone || "N/A"}</span></li>
                                        <li className="fs-4">Email: <span className="fw-semibold">{item?.property?.primary_email || "N/A"}</span></li>
                                    </ul>
                                    <span className="fw-bold fs-2">{item?.property?.name || "N/A"}</span>
                                </div>

                                {/* Registration Details */}
                                <div className="mt-5 px-4">
                                    <h3 className="fs-1 pb-2">Registration Form</h3>
                                    <div className="row mt-4">
                                        <div className="col-6">
                                            <ul className="list-unstyled">
                                                {[
                                                    { label: "Arrival Date", value: item?.checkin_date || "N/A" },
                                                    { label: "Reservation No", value: item?.invoice_number || "N/A" },
                                                    { label: "Rate Plan", value: item?.rate_plan?.name || "N/A" },
                                                    { label: "First Name", value: item?.customer_name || "N/A" },
                                                    { label: "Address", value: item?.customer_address || "N/A" },
                                                    { label: "State", value: item?.customer_state || "N/A" },
                                                    { label: "Country", value: item?.customer_country || "N/A" },
                                                    { label: "Nationality", value: item?.customer_country || "N/A" },
                                                    { label: "Arrival Time", value: "N/A" },
                                                    { label: "Passport / ID No", value: item?.customer_identity_number || "N/A" },
                                                    { label: "Vehicle Licence Plate & Model", value: "N/A" },
                                                ].map((field, indexField) => (
                                                    <li key={indexField} className="d-flex align-items-start mb-4" style={{ overflow: 'hidden' }}>
                                                        <span className="fw-semibold fs-4 flex-shrink-0" style={{ width: "220px" }}>{field.label}:</span>
                                                        <span className="border-bottom border-dark border-2 flex-grow-1 text-start fw-semibold" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', minWidth: 0 }}>
                                                            {typeof field.value === "object" ? JSON.stringify(field.value) : field.value}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="col-6">
                                            <ul className="list-unstyled">
                                                {[
                                                    { label: "Departure Date", value: item?.checkout_date || "N/A" },
                                                    { label: "Room No", value: item?.room?.room_number || "N/A" },
                                                    { label: "Room Type", value: item?.room_type?.name || "N/A" },
                                                    { label: "Last Name", value: item?.customer_surname || "N/A" },
                                                    { label: "City", value: item?.customer_city || "N/A" },
                                                    { label: "Zip/Postal Code", value: item?.customer_zip || "N/A" },
                                                    { label: "Telephone", value: item?.customer_phone || "N/A" },
                                                    { label: "Email", value: item?.customer_email || "N/A" },
                                                    { label: "Onward Destination", value: "N/A" },
                                                    { label: "Purpose of Visit", value: "N/A" },
                                                ].map((field, indexField) => (
                                                    <li key={indexField} className="d-flex align-items-start mb-3" style={{ overflow: 'hidden' }}>
                                                        <span className="fw-semibold fs-4 flex-shrink-0" style={{ width: "220px" }}>{field.label}:</span>
                                                        <span className="border-bottom border-dark border-2 flex-grow-1 text-start fw-semibold" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', minWidth: 0 }}>
                                                            {typeof field.value === "object" ? JSON.stringify(field.value) : field.value}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <div className="d-flex align-items-start mb-3" style={{ overflow: 'hidden' }}>
                                            <span className="fw-semibold fs-4 flex-shrink-0" style={{ width: "220px" }}>Additional Notes:</span>
                                            <span className="border-bottom border-dark border-2 flex-grow-1 text-start fw-semibold" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap', minWidth: 0 }}>
                                                {item?.public_note || "N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                 <div className="mt-5 pt-5 px-4 row container mx-auto" style={{ maxWidth: "700px", pageBreakInside: "avoid" }}>
                                    <div className="col-6 mb-3 px-6">
                                        <span className="fw-semibold fs-2 d-block mb-1 text-center">Issued By,</span>
                                        <div style={{ height: "100px" }}></div>
                                        <span className="border-bottom border-dark border-2 d-block w-100 pb-1"></span>
                                    </div>
                                    <div className="col-6 mb-3 px-6">
                                        <span className="fw-semibold fs-2 d-block mb-1 text-center">Received By,</span>
                                        <div style={{ height: "100px" }}></div>
                                        <span className="border-bottom border-dark border-2 d-block w-100 pb-1"></span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default RegistrationFormComponent;